import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "@phosphor-icons/react";

const CONTENT = {
  it: {
    title: "Condizioni d'uso · Otelya",
    desc: "Termini e condizioni per l'utilizzo della piattaforma Otelya, la receptionist AI multilingua per strutture ricettive italiane.",
    backHome: "Torna alla home",
    lastUpdated: "Ultimo aggiornamento: 29 settembre 2026",
    intro: [
      "Benvenuti in Otelya. Registrandovi e utilizzando la piattaforma accettate queste condizioni.",
      "Otelya è un servizio fornito da Alfio Moretti · Otelya · hello@otelya.app · Italia.",
    ],
    sections: [
      {
        h: "1. Cos'è Otelya",
        body: [
          "Otelya è una receptionist AI multilingua per strutture ricettive italiane. Automatizza risposte email, WhatsApp, telefono, prenotazioni, pubblicazione sui social e gestione DM.",
          "Il servizio è offerto in abbonamento mensile o annuale tramite Stripe.",
        ],
      },
      {
        h: "2. Account e sicurezza",
        body: [
          "Dovete fornire dati veritieri al momento della registrazione e mantenere aggiornati email e password.",
          "Siete responsabili di tutte le attività effettuate sotto il vostro account.",
          "In caso di sospetto accesso non autorizzato, scriveteci immediatamente a hello@otelya.app.",
        ],
      },
      {
        h: "3. Uso accettabile",
        body: [
          "Non usate Otelya per inviare spam, messaggi ingannevoli o contenuti illegali.",
          "Non tentate di aggirare limiti tecnici, di reverse-engineerare il servizio o di accedere a dati di altri utenti.",
          "Rispettate le policy di Meta, WhatsApp, Google e delle altre piattaforme integrate. La violazione può comportare la sospensione dell'integrazione.",
        ],
      },
      {
        h: "4. Contenuti e responsabilità",
        body: [
          "Rimanete titolari di tutti i contenuti che caricate su Otelya (testi, immagini, informazioni sulla struttura).",
          "Ci concedete una licenza limitata e non esclusiva per elaborare quei contenuti al fine di erogare il servizio (generare risposte AI, post, blog).",
          "Le risposte AI generate da Otelya sono suggerimenti: siete responsabili della validazione finale prima della pubblicazione o dell'invio agli ospiti quando la modalità supervisione è attiva.",
        ],
      },
      {
        h: "5. Pagamenti e cancellazione",
        body: [
          "Il pagamento avviene ricorrente tramite Stripe. La sottoscrizione si rinnova automaticamente salvo cancellazione dal Dashboard.",
          "Potete cancellare l'abbonamento in qualsiasi momento; il servizio resterà attivo fino alla fine del periodo pagato.",
          "Non sono previsti rimborsi per periodi già fatturati salvo obbligo di legge o errore imputabile a Otelya.",
        ],
      },
      {
        h: "6. Uptime e disponibilità",
        body: [
          "Ci impegniamo a offrire il servizio con la massima diligenza, con obiettivo di uptime del 99,5% mensile.",
          "Non siamo responsabili per interruzioni causate da fornitori terzi (Meta, Twilio, Stripe, OpenAI, Anthropic, Google) o da eventi di forza maggiore.",
        ],
      },
      {
        h: "7. Limitazione di responsabilità",
        body: [
          "Nella massima misura consentita dalla legge, Otelya non è responsabile per danni indiretti, perdita di profitto, perdita di prenotazioni causate da errori dell'AI, malfunzionamenti temporanei o disconnessioni delle integrazioni terze.",
          "La responsabilità totale di Otelya nei vostri confronti non potrà superare l'importo pagato nei 12 mesi precedenti l'evento.",
        ],
      },
      {
        h: "8. Cessazione",
        body: [
          "Potete chiudere l'account in qualsiasi momento contattando hello@otelya.app.",
          "Otelya può sospendere o chiudere l'account in caso di violazione di queste condizioni, con preavviso di 15 giorni salvo violazioni gravi.",
        ],
      },
      {
        h: "9. Legge applicabile",
        body: [
          "Queste condizioni sono regolate dalla legge italiana. Foro competente: Perugia.",
        ],
      },
      {
        h: "10. Contatti",
        body: [
          "Alfio Moretti · Otelya · hello@otelya.app",
        ],
      },
    ],
  },
  en: {
    title: "Terms of Service · Otelya",
    desc: "Terms and conditions for using Otelya, the multilingual AI receptionist for Italian hospitality businesses.",
    backHome: "Back to home",
    lastUpdated: "Last updated: September 29, 2026",
    intro: [
      "Welcome to Otelya. By signing up and using the platform, you accept these terms.",
      "Otelya is a service provided by Alfio Moretti · Otelya · hello@otelya.app · Italy.",
    ],
    sections: [
      {
        h: "1. What Otelya is",
        body: [
          "Otelya is a multilingual AI receptionist for Italian hospitality businesses. It automates email, WhatsApp, phone, booking replies, social publishing and DM handling.",
          "The service is offered as monthly or annual subscription via Stripe.",
        ],
      },
      {
        h: "2. Account and security",
        body: [
          "You must provide accurate information at signup and keep your email and password up to date.",
          "You are responsible for all activity under your account.",
          "In case of suspected unauthorized access, write to hello@otelya.app immediately.",
        ],
      },
      {
        h: "3. Acceptable use",
        body: [
          "Do not use Otelya to send spam, deceptive messages or illegal content.",
          "Do not attempt to bypass technical limits, reverse-engineer the service, or access other users' data.",
          "Respect the policies of Meta, WhatsApp, Google and other integrated platforms. Violation may result in integration suspension.",
        ],
      },
      {
        h: "4. Content and liability",
        body: [
          "You retain ownership of all content you upload to Otelya (text, images, property info).",
          "You grant us a limited, non-exclusive license to process that content to deliver the service (AI replies, posts, blog).",
          "AI-generated replies are suggestions: you are responsible for final validation before publishing or sending to guests when supervision mode is active.",
        ],
      },
      {
        h: "5. Payments and cancellation",
        body: [
          "Payment is recurring via Stripe. Subscription auto-renews unless cancelled from the Dashboard.",
          "You may cancel at any time; service remains active until the end of the paid period.",
          "No refunds for already-billed periods unless required by law or an Otelya-attributable error.",
        ],
      },
      {
        h: "6. Uptime and availability",
        body: [
          "We commit to deliver the service diligently, targeting 99.5% monthly uptime.",
          "We are not liable for interruptions caused by third-party providers (Meta, Twilio, Stripe, OpenAI, Anthropic, Google) or force majeure.",
        ],
      },
      {
        h: "7. Limitation of liability",
        body: [
          "To the maximum extent permitted by law, Otelya is not liable for indirect damages, loss of profits, or lost bookings caused by AI errors, temporary malfunctions or third-party disconnections.",
          "Otelya's total liability shall not exceed the amount paid in the 12 months preceding the event.",
        ],
      },
      {
        h: "8. Termination",
        body: [
          "You may close your account at any time by contacting hello@otelya.app.",
          "Otelya may suspend or close accounts for terms violations, with 15 days notice unless the violation is severe.",
        ],
      },
      {
        h: "9. Governing law",
        body: [
          "These terms are governed by Italian law. Venue: Perugia.",
        ],
      },
      {
        h: "10. Contact",
        body: [
          "Alfio Moretti · Otelya · hello@otelya.app",
        ],
      },
    ],
  },
};

function useLang() {
  const q = new URLSearchParams(useLocation().search).get("lang")?.toLowerCase();
  if (q === "en" || q === "it") return q;
  return (typeof navigator !== "undefined" && (navigator.language || "it").toLowerCase().startsWith("en")) ? "en" : "it";
}

export default function Terms() {
  const lang = useLang();
  const c = CONTENT[lang] || CONTENT.it;
  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="terms-page">
      <Helmet>
        <html lang={lang} />
        <title>{c.title}</title>
        <meta name="description" content={c.desc} />
        <link rel="canonical" href="https://otelya.app/terms" />
        <link rel="alternate" hrefLang="it-IT" href="https://otelya.app/terms" />
        <link rel="alternate" hrefLang="en-US" href="https://otelya.app/terms?lang=en" />
        <meta property="og:title" content={c.title} />
        <meta property="og:description" content={c.desc} />
        <meta property="og:url" content="https://otelya.app/terms" />
      </Helmet>

      <nav className="border-b border-line/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" data-testid="terms-back-home">
            <Logo />
          </Link>
          <Link to={`/?lang=${lang}`} className="text-sm text-stone hover:text-terracotta transition-colors flex items-center gap-1">
            <ArrowLeft size={14} /> {c.backHome}
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-ink">
          {c.title.split(" · ")[0]}
        </h1>
        <p className="mt-3 text-sm text-stone italic">{c.lastUpdated}</p>

        <div className="mt-10 space-y-5">
          {c.intro.map((p, i) => (
            <p key={i} className="text-base sm:text-lg text-ink leading-[1.75]">{p}</p>
          ))}
        </div>

        <div className="mt-10 space-y-12">
          {c.sections.map((s, i) => (
            <section key={i} data-testid={`terms-section-${i}`}>
              <h2 className="font-serif text-2xl lg:text-3xl mb-4 text-ink">{s.h}</h2>
              <ul className="space-y-3 list-disc pl-6">
                {s.body.map((b, j) => (
                  <li key={j} className="text-base text-ink leading-[1.75]">{b}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-12 border-t border-line/60 text-sm text-stone">
          <Link to="/privacy" className="underline hover:text-terracotta">
            {lang === "en" ? "Privacy Policy" : "Informativa sulla privacy"}
          </Link>
          {" · "}
          <a href="mailto:hello@otelya.app" className="hover:text-terracotta">hello@otelya.app</a>
        </div>
      </article>
    </div>
  );
}
